import React, { useState, useEffect, useRef } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { useSwipeable } from 'react-swipeable';
import { isGameFinished } from '../utils/constants';
import { isBinaryMarket as isBinaryMarketUtil, getEdgeForPlayerBook, recalculateEdge } from '../utils/oddsHelpers';
import GameHeader from './GameHeader';
import UpgradeButton from './UpgradeButton';

// Helper to abbreviate basketball positions
const abbreviatePosition = (position) => {
  if (!position) return null;
  const pos = typeof position === 'object' ? (position.abbreviation || position.name) : position;
  if (!pos) return null;

  const abbrevMap = {
    'Point Guard': 'PG',
    'Shooting Guard': 'SG',
    'Small Forward': 'SF',
    'Power Forward': 'PF',
    'Center': 'C',
    'Forward': 'F',
    'Guard': 'G',
    'Guard-Forward': 'G-F',
    'Forward-Guard': 'F-G',
    'Forward-Center': 'F-C',
    'Center-Forward': 'C-F'
  };

  return abbrevMap[pos] || pos;
};

// Helper to check if a player is in the starting lineup
const isPlayerInLineup = (playerName, playerTeam, game, playerId) => {
  if (!game) return false;

  // Helper to check if a lineup entry matches by playerId or last name
  const matchesPlayer = (entry) => {
    if (!entry) return false;
    if (typeof entry === 'object' && entry.playerId && playerId) {
      return String(entry.playerId) === String(playerId);
    }
    const lineupName = typeof entry === 'object' ? entry.name : entry;
    if (!lineupName || typeof lineupName !== 'string' || !playerName) return false;
    const nameParts = playerName.trim().split(' ');
    const playerLastName = nameParts[nameParts.length - 1].toLowerCase();
    const lineupLastName = lineupName.trim().split(' ').pop().toLowerCase();
    return lineupLastName === playerLastName;
  };

  const checkLineup = (lineup) => {
    if (!lineup) return false;
    if (typeof lineup === 'object' && !Array.isArray(lineup)) {
      return Object.values(lineup).some(matchesPlayer);
    }
    if (Array.isArray(lineup)) {
      return lineup.some(matchesPlayer);
    }
    return false;
  };

  // Try team-based lookup first
  const isAway = playerTeam === game.away || (playerTeam && playerTeam === game.awayTeamId);
  const isHome = playerTeam === game.home || (playerTeam && playerTeam === game.homeTeamId);
  if (isAway) return checkLineup(game.awayLineup);
  if (isHome) return checkLineup(game.homeLineup);

  // Team didn't match — check both lineups (playerIds are unique)
  return checkLineup(game.awayLineup) || checkLineup(game.homeLineup);
};

// Helper to get player news/injury status
// Checks game.news first (keyed by playerId), falls back to injury arrays
const getPlayerInjury = (playerId, playerName, playerTeam, game) => {
  if (!game) return null;
  // Check news object first (keyed by playerId)
  if (game.news && playerId && game.news[String(playerId)]) {
    const entry = game.news[String(playerId)];
    const latest = entry.updates && entry.updates[0];
    if (latest) {
      const s = (latest.status || '').toLowerCase();
      return {
        status: latest.status,
        details: latest.bodyPart,
        type: s.includes('out') ? 'out' : s.includes('questionable') ? 'questionable' : s.includes('doubtful') ? 'doubtful' : s
      };
    }
  }
  // Fall back to injuries arrays
  const isAway = playerTeam === game.away || (playerTeam && playerTeam === game.awayTeamId);
  const isHome = playerTeam === game.home || (playerTeam && playerTeam === game.homeTeamId);
  const injuries = isAway ? game.awayInjuries : isHome ? game.homeInjuries : null;
  if (!injuries || !injuries.length) return null;
  return injuries.find(inj =>
    (inj.playerId && playerId && String(inj.playerId) === String(playerId)) ||
    inj.playerName === playerName
  ) || null;
};

// Maps injury type to badge background color
const getInjuryColor = (type) => {
  switch (type) {
    case 'out': return '#dc3545';
    case 'questionable': return '#000000';
    case 'doubtful': return '#6c757d';
    default: return '#0d6efd';
  }
};

// Renders position abbreviation with injury-aware styling
// Starters stay yellow text, Out=red bg, Questionable=black bg, Doubtful=grey bg, other=blue bg
const renderPositionBadge = (player, game) => {
  const pos = abbreviatePosition(player.positionCode || player.position);
  if (!pos) return null;
  const injury = getPlayerInjury(player.playerId, player.name, player.team || player.teamId, game);
  if (injury) {
    const bg = getInjuryColor(injury.type);
    const inLineup = isPlayerInLineup(player.name, player.team || player.teamId, game, player.playerId);
    return (
      <span
        title={`${injury.status}${injury.details ? ' - ' + injury.details : ''}`}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: '22px', height: '16px', borderRadius: '8px',
          background: bg, color: inLineup ? '#ffc107' : 'white',
          fontSize: '0.65em', fontWeight: 'bold',
          marginLeft: '4px', padding: '0 5px', flexShrink: 0, cursor: 'help'
        }}
      >{pos}</span>
    );
  }
  const inLineup = isPlayerInLineup(player.name, player.team || player.teamId, game, player.playerId);
  return (
    <span style={{
      fontSize: '0.75em',
      color: inLineup ? '#ffc107' : '#6c757d',
      fontWeight: inLineup ? 'bold' : 'normal',
      marginLeft: '4px'
    }}>({pos})</span>
  );
};

const BasketballDataTable = ({
  data,
  gameTime,
  gameStatus,
  gameDate,
  lastUpdate,
  game,
  formatPlayerName,
  getTeamLogoUrl,
  getBookLogoUrl,
  isMobile,
  selectedMarket,
  selectedMarketName,
  bookmakers,
  isLoading,
  availableMarkets,
  onMarketSelection,
  showTiers,
  onTierToggle,
  masterTierFilter,
  onPlayerClick,
  onOddsClick,
  onOddsContextMenu,
  onPlayerContextMenu,
  clickedCellId,
  isCollapsed = false,
  onCollapseToggle,
  onSettingsClick,
  nbaViewMode = 'ou', // 'ou' for O/U view, 'bestline' for Best Line view
  recalcEdgeOdds = false
}) => {
  // Debug logging
  console.log('BasketballDataTable props:', { nbaViewMode, gameSport: game?.sport, hasData: !!data, dataLength: data?.length, selectedMarket, gameId: game?.gameIDmlb });

  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', sport: 'wnba', ...game };

  // Helper for right-clicking a player name — wires the App-level
  // context menu (e.g. "Add to Scratchpad").
  const handleNameContextMenu = (player) => (e) => {
    if (!onPlayerContextMenu) return;
    const teamAbbr =
      player?.teamAbv ||
      (player?.team === 'away' ? safeGame.away : player?.team === 'home' ? safeGame.home : player?.team) ||
      player?.teamId ||
      null;
    onPlayerContextMenu(e, safeGame.gameIDmlb, player?.playerId, player?.name, teamAbbr);
  };

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  // Track rendering state - prevent heavy processing until spinner paints
  const [isRendering, setIsRendering] = useState(false);
  const prevMarketRef = useRef(selectedMarket);

  // Mobile carousel state
  const [mobileBookIndex, setMobileBookIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Use provided bookmakers or fall back to default list
  const allBookmakers = bookmakers && bookmakers.length > 0 ? bookmakers : ['fanduel', 'draftkings', 'betmgm', 'fanatics'];
  const booksPerPage = 5;
  const totalPages = Math.ceil(allBookmakers.length / booksPerPage);

  // Clear rendering state after spinner has painted
  useEffect(() => {
    if (isRendering) {
      const timeoutId = setTimeout(() => {
        setIsRendering(false);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isRendering]);

  // Navigation functions for mobile (defined before swipe handlers that use them)
  const goToPrevBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === 0 ? totalPages - 1 : prev - 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const goToNextBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === totalPages - 1 ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Swipe handlers for mobile
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (isMobile && totalPages > 1) {
        goToNextBooks();
      }
    },
    onSwipedRight: () => {
      if (isMobile && totalPages > 1) {
        goToPrevBooks();
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  // Safety check - if selectedMarket is undefined, show loading (AFTER all hooks)
  if (!selectedMarket) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (safeGame.gameIDmlb === '401810219') {
    console.log('NBA 401810219 debug:', {
      selectedMarket,
      hasData: !!data,
      dataLength: data?.length,
      firstPlayer: data?.[0],
      sampleKeys: data?.[0] ? Object.keys(data[0]).slice(0, 20) : []
    });
  }

  // Helper function to create cell ID for animation
  const createCellId = (gameId, playerId, market, bookmaker, line, side) => {
    return `${gameId}_${playerId}_${market}_${bookmaker}_${line}_${side}`;
  };

  // Helper function to check if cell should be animated
  const getCellClassName = (gameId, playerId, market, bookmaker, line, side) => {
    const cellId = createCellId(gameId, playerId, market, bookmaker, line, side);
    return cellId === clickedCellId ? 'bet-add-animation' : '';
  };

  // Detect market change DURING render (not in useEffect)
  if (prevMarketRef.current !== selectedMarket) {
    if (!isRendering) {
      // Market just changed, set rendering state synchronously
      setIsRendering(true);
    }
    prevMarketRef.current = selectedMarket;
  }

  // Combined loading state
  const showLoading = isLoading || isRendering;

  // Block rendering heavy data while loading
  const displayData = showLoading ? [] : data;

  // Get visible books based on platform
  const bookLogos = isMobile
    ? allBookmakers.slice(mobileBookIndex * booksPerPage, (mobileBookIndex + 1) * booksPerPage)
    : allBookmakers;

  const oddsCellStyle = {
    width: '80px',
    minWidth: '80px',
    maxWidth: '80px',
    textAlign: 'center',
    fontSize: '0.95rem',
    letterSpacing: '-0.025em',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  // Use shared utility for consistency with Best Bets
  const isBinaryMarket = isBinaryMarketUtil;

  // Best odds styling function
  const getCellStyle = (price, bestBookPrice, bestBookDiff, count) => {
    const style = {};
    const occurrences = [price].filter(p => p === bestBookPrice).length;

    // Always bold best odds when there are multiple books and it's the unique best price
    if (price === bestBookPrice && count > 1 && occurrences === 1) {
      style.fontWeight = 'bold';

      // Apply edge tier colors if they meet the criteria
      if (bestBookDiff !== 1) {
        const diffValue = Math.abs(bestBookDiff);
        if (diffValue >= 0.25) {
          // Tier 1: Green with yellow text
          Object.assign(style, { backgroundColor: 'green', color: 'yellow' });
        } else if (diffValue >= 0.15) {
          // Tier 2: Amber with navy blue text
          Object.assign(style, { backgroundColor: '#F59E0B', color: '#1E40AF' });
        } else if (diffValue >= 0.05) {
          // Tier 3: Blue
          Object.assign(style, { backgroundColor: '#3B82F6', color: 'white' });
        } else {
          // Best line but below tier thresholds - use amber color
          Object.assign(style, { color: '#F59E0B' });
        }
      }
    }

    return style;
  };

  const getTooltipText = (price, bestBookPrice, bestBookDiff, count) => {
    const occurrences = [price].filter(p => p === bestBookPrice).length;
    if (price === bestBookPrice && bestBookDiff !== 1 && count > 1 && occurrences === 1) {
      const diffValue = Math.abs(bestBookDiff);
      if (diffValue > 0) {
        return `${(diffValue * 100).toFixed(1)}% edge`;
      }
    }
    return null;
  };

  // Calculate best line for a player/book combo based on edge (for Best Line view)
  // Takes all lines for a player and finds the one with best edge for the specified book
  // Recalculates edges by comparing this book's odds against ALL books for each point value
  const getBestLineByEdge = (allPlayerLines, book, market) => {
    let bestEdgeLine = null;
    let bestEdgeDiff = -Infinity;
    let highestPointLine = null;
    let highestPoint = -Infinity;

    // Debug logging for specific case
    const playerName = allPlayerLines[0]?.name;
    const shouldLog = playerName === 'Caris LeVert' && book === 'draftkings' && market === 'player_threes';

    // Always log if it's Caris LeVert to see what's happening
    if (playerName === 'Caris LeVert' && book === 'draftkings') {
      console.log('getBestLineByEdge called for Caris LeVert:', { book, market, numLines: allPlayerLines.length });
    }

    // Group all lines by point value (combining mainlines and alternates)
    const linesByPoint = {};
    allPlayerLines.forEach(line => {
      const point = line.point;
      if (!linesByPoint[point]) {
        linesByPoint[point] = [];
      }
      linesByPoint[point].push(line);
    });

    // For each point value, calculate edge for this specific book
    Object.entries(linesByPoint).forEach(([point, linesAtPoint]) => {
      // Get this book's OVER odds for this point value (check all lines at this point)
      let thisBookOverOdds = null;
      let thisBookPoint = null;
      let isAlternate = false;

      for (const line of linesAtPoint) {
        const overOdds = line[`${market}_${book}_over`];
        const bookPoint = line[`${market}_${book}_point`] || line.point;

        if (overOdds !== null && overOdds !== undefined) {
          thisBookOverOdds = overOdds;
          thisBookPoint = bookPoint;
          isAlternate = line.isAlternate;
        }
      }

      // Skip if this book has no OVER odds for this point value
      if (thisBookOverOdds === null) {
        return;
      }

      // Skip if odds are -200 or worse (only consider odds > -200)
      if (thisBookOverOdds <= -200) {
        return;
      }

      // Find the best OVER odds across ALL books for this point value
      let bestOverOdds = -Infinity;
      let bestOverOddsCount = 0;
      let secondBestOverOdds = -Infinity;

      linesAtPoint.forEach(line => {
        allBookmakers.forEach(b => {
          const overOdds = line[`${market}_${b}_over`];

          if (overOdds !== null && overOdds !== undefined) {
            if (overOdds > bestOverOdds) {
              secondBestOverOdds = bestOverOdds;
              bestOverOdds = overOdds;
              bestOverOddsCount = 1;
            } else if (overOdds === bestOverOdds) {
              bestOverOddsCount++;
            } else if (overOdds > secondBestOverOdds) {
              secondBestOverOdds = overOdds;
            }
          }
        });
      });

      // Calculate edge for this book's over odds (ONLY if this book UNIQUELY has best odds)
      if (thisBookOverOdds === bestOverOdds && bestOverOddsCount === 1) {
        // If at least 2 books have odds, calculate real edge; otherwise edge is 0 (display but don't color)
        let edgeDiff = 0;
        if (secondBestOverOdds > -Infinity) {
          // Handle cross-zero comparison (positive best odds vs negative second best)
          if (bestOverOdds > 0 && secondBestOverOdds < 0) {
            const spreadPositive = bestOverOdds - 100;  // Distance from +100
            const spreadNegative = secondBestOverOdds - (-100);  // Distance from -100 (will be negative)
            const totalSpread = spreadPositive - spreadNegative;  // Total spread through baseline
            edgeDiff = Math.abs(totalSpread / secondBestOverOdds);
          } else {
            edgeDiff = (bestOverOdds - secondBestOverOdds) / Math.abs(secondBestOverOdds);
          }
        }

        if (shouldLog) {
          console.log(`Point ${point} over:`, {
            thisBookOdds: thisBookOverOdds,
            bestOdds: bestOverOdds,
            bestOddsCount: bestOverOddsCount,
            secondBest: secondBestOverOdds,
            edgeDiff,
            isAlternate,
            onlyOneBook: secondBestOverOdds === -Infinity
          });
        }

        // Only select lines with REAL edges (edgeDiff > 0)
        const currentPoint = parseFloat(point);
        if (edgeDiff > 0 && edgeDiff > bestEdgeDiff) {
          bestEdgeDiff = edgeDiff;
          bestEdgeLine = {
            odds: thisBookOverOdds,
            point: currentPoint,
            side: 'over',
            bestBookPrice: bestOverOdds,
            bestBookDiff: edgeDiff
          };
          if (shouldLog) {
            console.log(`  ✓ New best edge: ${edgeDiff} at ${currentPoint}`);
          }
        }
      }

      // Track highest point MAINLINE as fallback when no edges exist (only if odds > -200)
      // Don't use alternates as fallback - only show alternates if they have real edges
      const currentPoint = parseFloat(point);
      if (!isAlternate && currentPoint > highestPoint && thisBookOverOdds !== null && thisBookOverOdds > -200) {
        highestPoint = currentPoint;
        highestPointLine = {
          overOdds: thisBookOverOdds,
          bookPoint: currentPoint,
          bestBookPrice: null,
          bestBookDiff: 0
        };
      }
    });

    if (shouldLog) {
      console.log('Final result:', bestEdgeLine || highestPointLine || 'null');
    }

    // If we found a line with edge, return it
    if (bestEdgeLine) {
      return bestEdgeLine;
    }

    // Otherwise, return the highest point non-alternate line (only if it has OVER odds)
    if (highestPointLine) {
      const { overOdds, bookPoint } = highestPointLine;

      if (overOdds !== null && overOdds !== undefined) {
        return {
          odds: overOdds,
          point: bookPoint,
          side: 'over',
          bestBookPrice: null,
          bestBookDiff: 0
        };
      }
    }

    // No valid line found for this book
    return null;
  };

  // Get best line for a player at a specific book (for Best Line view with flat player objects)
  // This handles player objects that have odds directly as properties (not .lines array)
  const getBestLine = (player, book, market) => {
    const overOdds = player[`${market}_${book}_over`];
    const underOdds = player[`${market}_${book}_under`];
    const point = player[`${market}_${book}_point`] || player.point;

    // Return over if available, otherwise under
    if (overOdds !== null && overOdds !== undefined) {
      return { odds: overOdds, point, side: 'over' };
    } else if (underOdds !== null && underOdds !== undefined) {
      return { odds: underOdds, point, side: 'under' };
    }
    return null;
  };

  // Get best line edge data across all books for tier highlighting (for Best Line view)
  // Returns { bestOdds, bestOddsCount, diff } for determining tier colors
  const getBestLineEdge = (player, market, allBooks) => {
    let bestOdds = -Infinity;
    let bestOddsCount = 0;
    let secondBestOdds = -Infinity;

    allBooks.forEach(book => {
      const overOdds = player[`${market}_${book}_over`];
      if (overOdds !== null && overOdds !== undefined) {
        if (overOdds > bestOdds) {
          secondBestOdds = bestOdds;
          bestOdds = overOdds;
          bestOddsCount = 1;
        } else if (overOdds === bestOdds) {
          bestOddsCount++;
        } else if (overOdds > secondBestOdds) {
          secondBestOdds = overOdds;
        }
      }
    });

    if (bestOdds === -Infinity) return null;

    // Calculate edge difference using same logic as getBestLineByEdge
    let diff = 0;
    if (secondBestOdds > -Infinity) {
      if (bestOdds > 0 && secondBestOdds < 0) {
        const spreadPositive = bestOdds - 100;
        const spreadNegative = secondBestOdds - (-100);
        const totalSpread = spreadPositive - spreadNegative;
        diff = Math.abs(totalSpread / secondBestOdds);
      } else {
        diff = (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
      }
    }

    return { bestOdds, bestOddsCount, diff };
  };

  // Calculate mobile column width based on number of bookmakers
  const mobileBookWidth = Math.max(12, Math.floor(60 / bookLogos.length));
  const mobileNameWidth = 100 - (mobileBookWidth * bookLogos.length);

  const formatTime = (timeStr, gameStatus) => {
    if (!timeStr) return '';
    
    // If time is TBD, display TBD
    if (timeStr === 'TBD' || timeStr === 'TBA') return timeStr;
    
    // If the game is finished, don't show time
    if (gameStatus && isGameFinished(gameStatus)) {
      return '';
    }
    
    // For live games, show "LIVE" instead of time
    if (gameStatus === 'Live' || gameStatus === 'In Progress' || gameStatus === 'InProgress') {
      return 'LIVE';
    }
    
    // Handle PM/AM times - try to parse as Date first
    try {
      // If it looks like a time string (contains ':'), try to format it
      if (timeStr.includes(':')) {
        return timeStr; // Return as-is for now
      }
      return timeStr;
    } catch (e) {
      return timeStr;
    }
  };

  // Basketball-specific live status formatter
  const formatBasketballLiveStatus = (currentStatus, game, safeGame) => {
    const period = game.period || safeGame.period;
    const clock = game.clock || safeGame.clock;
    
    // Format quarter display for basketball
    let quarterDisplay = '';
    if (!period) {
      // No period data, just show LIVE
      return (
        <span style={{ 
          backgroundColor: 'red', 
          color: 'white', 
          padding: '2px 6px', 
          borderRadius: '4px',
          fontWeight: 'bold',
          fontSize: '0.75rem',
          display: 'inline-block'
        }}>
          LIVE
        </span>
      );
    } else if (period === 1) {
      quarterDisplay = '1st';
    } else if (period === 2) {
      quarterDisplay = '2nd';
    } else if (period === 3) {
      quarterDisplay = '3rd';
    } else if (period === 4) {
      quarterDisplay = '4th';
    } else if (period === 5) {
      quarterDisplay = 'OT';
    } else if (period > 5) {
      quarterDisplay = `OT${period - 4}`;
    }
    
    return (
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        fontSize: '1.4rem',
        fontWeight: '600',
        color: 'yellow'
      }}>
        <div>{quarterDisplay}</div>
        {clock && <div style={{ fontSize: '1.3rem', fontWeight: '500' }}>{clock}</div>}
      </div>
    );
  };

  // Check if data is empty or loading
  if (!displayData || displayData.length === 0) {
    if (showLoading) {
      return (
        <div className="table-group">
          <GameHeader 
            gameTime={gameTime}
          gameDate={gameDate}
            gameStatus={gameStatus}
            game={game}
            getTeamLogoUrl={getTeamLogoUrl}
            isMobile={isMobile}
            isCollapsed={isCollapsed}
            onCollapseToggle={onCollapseToggle}
            formatLiveStatus={formatBasketballLiveStatus}
            showScores={true}
            isLoading={showLoading}
          />
          <div className="text-center p-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="table-group">
        <GameHeader 
          gameTime={gameTime}
          gameDate={gameDate}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={isMobile}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatBasketballLiveStatus}
          showScores={true}
            isLoading={showLoading}
        />
        <div className="text-center p-3 text-muted">
          {(game?.gameIDmlb?.startsWith('locked_') || game?.gameId?.startsWith('locked_')) ? (
            <UpgradeButton onClick={onSettingsClick} />
          ) : (
            'No data available for this market'
          )}
        </div>
      </div>
    );
  }

  // Filter data based on showTiers setting and master tier filter
  let filteredData = displayData;

  if (masterTierFilter !== null) {
    filteredData = filteredData.filter(player => {
      const playerTier = parseFloat(player.point);
      return playerTier === masterTierFilter;
    });
  } else {
    // Standard tier filtering logic for basketball
    // Only filter out alternates if there are non-alternate lines available
    // IMPORTANT: In Best Line mode, we need ALL alternates to find the best edge, so skip alternate filtering
    const hasNonAlternates = data.some(player => !player.isAlternate);
    filteredData = (showTiers || nbaViewMode === 'bestline') ? filteredData : (hasNonAlternates ? filteredData.filter(player => !player.isAlternate) : filteredData);
  }

  // Sort the filtered data - away team players first, then home team players
  const sortedData = [...filteredData].sort((a, b) => {
    // First sort by team (away team first)
    const aTeam = a.team || '';
    const bTeam = b.team || '';
    if (aTeam === game.away_team && bTeam === game.home_team) return -1;
    if (aTeam === game.home_team && bTeam === game.away_team) return 1;

    // Then sort by name (handle both name and playerName fields)
    const aName = a.name || a.playerName || '';
    const bName = b.name || b.playerName || '';
    const nameComparison = aName.localeCompare(bName);
    if (nameComparison !== 0) return nameComparison;

    // Then by point value (ascending)
    const aPoint = parseFloat(a.point) || 0;
    const bPoint = parseFloat(b.point) || 0;
    return aPoint - bPoint;
  });

  // Mainline markets (h2h, totals, spreads) - render similar to NFL
  if (['h2h', 'totals', 'spreads'].includes(selectedMarket)) {
    // Handle h2h data like NFLDataTable
    let h2hData = [];
    let totalsData = [];
    let spreadsData = [];

    if (selectedMarket === 'h2h') {
      // When h2h is selected, the endpoint returns all three markets at once
      // So populate all three arrays from the filteredData
      filteredData.forEach(player => {
        if (player.playerId === 'team_away' || player.playerId === 'team_home') {
          h2hData.push(player);
        } else if (player.playerId?.startsWith('totals_')) {
          totalsData.push(player);
        } else if (player.playerId?.startsWith('spreads_')) {
          spreadsData.push(player);
        }
      });

      // Sort h2h data: away team first, then home team
      h2hData.sort((a, b) => {
        if (a.team === 'away' && b.team === 'home') return -1;
        if (a.team === 'home' && b.team === 'away') return 1;
        return 0;
      });
    } else if (selectedMarket === 'totals') {
      totalsData = filteredData.filter(p => p.playerId?.startsWith('totals_'));
    } else if (selectedMarket === 'spreads') {
      spreadsData = filteredData.filter(p => p.playerId?.startsWith('spreads_'));
    }
    

    return (
      <div className="table-group">
        <GameHeader 
          gameTime={gameTime}
          gameDate={gameDate}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={isMobile}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatBasketballLiveStatus}
          showScores={true}
            isLoading={showLoading}
        />
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Moneyline Table */}
        {h2hData.length > 0 && (
          <div className="table-container">
            <h6 className="text-center mb-2" style={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'rgb(245, 158, 11)' }}>
              Moneyline
            </h6>
            <Table bordered size="sm" className="text-center">
              <thead>
                <tr>
                  <th style={{ width: '120px' }}>TEAM</th>
                  {bookLogos.map(book => {
                    const bookLink = safeGame.sgoLinks?.bookmakers?.[book];
                    return (
                      <th key={`h2h-${book}`} style={oddsCellStyle}>
                        {bookLink ? (
                          <a
                            href={bookLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-block',
                              touchAction: 'auto',
                              WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                              padding: '4px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('H2H logo clicked:', book, bookLink);
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <img
                              src={getBookLogoUrl(book)}
                              alt={`${book} logo`}
                              title={book}
                              style={{
                                height: '20px',
                                cursor: 'pointer',
                                pointerEvents: 'auto',
                                display: 'block'
                              }}
                            />
                          </a>
                        ) : (
                          <img
                            src={getBookLogoUrl(book)}
                            alt={`${book} logo`}
                            title={book}
                            style={{ height: '20px' }}
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {h2hData.map((team, index) => {
                  // Get team data from game
                  const teamAbbrev = team.team === 'away' ? safeGame.away : safeGame.home;
                  const teamNickname = team.team === 'away' ? safeGame.awayNickname : safeGame.homeNickname;

                  return (
                  <tr key={`h2h-${index}`}>
                    <td style={{ textAlign: 'left', padding: '8px', fontWeight: 'bold' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <img
                          src={getTeamLogoUrl(teamAbbrev)}
                          alt={teamAbbrev}
                          style={{ width: '16px', height: '16px' }}
                          onError={(e) => {
                            e.target.style.visibility = 'hidden';
                            e.target.style.width = '0px';
                            e.target.style.height = '0px';
                          }}
                        />
                        {teamNickname || teamAbbrev}
                      </div>
                    </td>
                    {bookLogos.map(book => {
                      const odds = team[`h2h_${book}`];
                      const bestBookPrice = team['h2h_bestBookPrice'];
                      const bestBookDiff = recalcEdgeOdds
                        ? recalculateEdge(team, book, 'h2h', allBookmakers, null)
                        : team['h2h_bestBookDiff'];
                      const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, bookLogos.length) : {};

                      return (
                        <td key={`h2h-${book}-${index}`} style={{...oddsCellStyle, ...cellStyle}}>
                          {odds ? (
                            <div style={{ fontSize: '0.9rem' }}>
                              {odds > 0 ? `+${odds}` : odds}
                            </div>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        {/* Totals Table */}
        {totalsData.length > 0 && (
          <div className="table-container">
            <Table bordered size="sm" className="text-center">
              <thead>
                <tr><th colSpan={bookLogos.length + 1} style={{ backgroundColor: 'transparent', color: 'rgb(245, 158, 11)', fontWeight: 'bold' }}>Totals</th></tr>
                <tr>
                  <th style={{ width: '120px' }}></th>
                  {bookLogos.map(book => {
                    const bookLink = safeGame.sgoLinks?.bookmakers?.[book];
                    return (
                      <th key={`totals-${book}`} style={oddsCellStyle}>
                        {bookLink ? (
                          <a
                            href={bookLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-block',
                              touchAction: 'auto',
                              WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                              padding: '4px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('Totals logo clicked:', book, bookLink);
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <img
                              src={getBookLogoUrl(book)}
                              alt={`${book} logo`}
                              title={book}
                              style={{
                                height: '20px',
                                cursor: 'pointer',
                                pointerEvents: 'auto',
                                display: 'block'
                              }}
                            />
                          </a>
                        ) : (
                          <img
                            src={getBookLogoUrl(book)}
                            alt={`${book} logo`}
                            title={book}
                            style={{ height: '20px' }}
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {/* Over Row */}
                <tr>
                  <td style={{ width: '120px', fontWeight: 'bold' }}>
                    Over
                  </td>
                  {bookLogos.map(book => {
                    const overData = totalsData.find(d =>
                      d.betType === 'over' &&
                      d[`totals_${book}`] !== null &&
                      d[`totals_${book}`] !== undefined
                    );
                    const odds = overData?.[`totals_${book}`];
                    const bestBookPrice = overData?.['totals_bestBookPrice'];
                    const bestBookDiff = recalcEdgeOdds && overData
                      ? recalculateEdge(overData, book, 'totals', allBookmakers, null)
                      : overData?.['totals_bestBookDiff'];
                    const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, bookLogos.length) : {};

                    return (
                      <td key={`totals-${book}-over`} style={{...oddsCellStyle, ...cellStyle, textAlign: 'center', padding: '8px 4px', fontSize: '0.8rem'}}>
                        {overData ? (
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{odds > 0 ? `+${odds}` : odds}</div>
                            <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>o{overData.point}</div>
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>

                {/* Under Row */}
                <tr>
                  <td style={{ width: '120px', fontWeight: 'bold' }}>
                    Under
                  </td>
                  {bookLogos.map(book => {
                    // Find the over line for this bookmaker
                    const overData = totalsData.find(d =>
                      d.betType === 'over' &&
                      d[`totals_${book}`] !== null &&
                      d[`totals_${book}`] !== undefined
                    );
                    // Find under with the same point value
                    const underData = overData ?
                      totalsData.find(d =>
                        d.betType === 'under' &&
                        d.point === overData.point &&
                        d[`totals_${book}`] !== null &&
                        d[`totals_${book}`] !== undefined
                      ) : null;
                    const odds = underData?.[`totals_${book}`];
                    const bestBookPrice = underData?.['totals_bestBookPrice'];
                    const bestBookDiff = recalcEdgeOdds && underData
                      ? recalculateEdge(underData, book, 'totals', allBookmakers, null)
                      : underData?.['totals_bestBookDiff'];
                    const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, bookLogos.length) : {};

                    return (
                      <td key={`totals-${book}-under`} style={{...oddsCellStyle, ...cellStyle, textAlign: 'center', padding: '8px 4px', fontSize: '0.8rem'}}>
                        {underData ? (
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>u{underData.point}</div>
                            <div style={{ fontWeight: 'bold' }}>{odds > 0 ? `+${odds}` : odds}</div>
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </Table>
          </div>
        )}

        {/* Spreads Table */}
        {spreadsData.length > 0 && (
          <div className="table-container">
            <Table bordered size="sm" className="text-center">
              <thead>
                <tr><th colSpan={bookLogos.length + 1} style={{ backgroundColor: 'transparent', color: 'rgb(245, 158, 11)', fontWeight: 'bold' }}>Spreads</th></tr>
                <tr>
                  <th style={{ width: '120px' }}></th>
                  {bookLogos.map(book => {
                    const bookLink = safeGame.sgoLinks?.bookmakers?.[book];
                    return (
                      <th key={`spreads-${book}`} style={oddsCellStyle}>
                        {bookLink ? (
                          <a
                            href={bookLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-block',
                              touchAction: 'auto',
                              WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                              padding: '4px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('Spreads logo clicked:', book, bookLink);
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <img
                              src={getBookLogoUrl(book)}
                              alt={`${book} logo`}
                              title={book}
                              style={{
                                height: '20px',
                                cursor: 'pointer',
                                pointerEvents: 'auto',
                                display: 'block'
                              }}
                            />
                          </a>
                        ) : (
                          <img
                            src={getBookLogoUrl(book)}
                            alt={`${book} logo`}
                            title={book}
                            style={{ height: '20px' }}
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ width: '120px', fontWeight: 'bold' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <img
                        src={getTeamLogoUrl(safeGame.away)}
                        alt={safeGame.away}
                        style={{ width: '16px', height: '16px' }}
                        onError={(e) => {
                          e.target.style.visibility = 'hidden';
                          e.target.style.width = '0px';
                          e.target.style.height = '0px';
                          e.target.style.margin = '0px';
                        }}
                      />
                      <span>{safeGame.away}</span>
                    </div>
                  </td>
                  {bookLogos.map(book => {
                    const awayData = spreadsData.find(d => d.team === 'away' && d[`spreads_${book}`]);
                    const odds = awayData?.[`spreads_${book}`];
                    const bestBookPrice = awayData?.['spreads_bestBookPrice'];
                    const bestBookDiff = recalcEdgeOdds && awayData
                      ? recalculateEdge(awayData, book, 'spreads', allBookmakers, null)
                      : awayData?.['spreads_bestBookDiff'];
                    const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, bookLogos.length) : {};

                    return (
                      <td key={`spreads-${book}-away`} style={{...oddsCellStyle, ...cellStyle, textAlign: 'center', padding: '8px 4px', fontSize: '0.8rem'}}>
                        {awayData ? (
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{odds > 0 ? `+${odds}` : odds}</div>
                            <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{awayData.point > 0 ? `+${awayData.point}` : awayData.point}</div>
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td style={{ width: '120px', fontWeight: 'bold' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <img
                        src={getTeamLogoUrl(safeGame.home)}
                        alt={safeGame.home}
                        style={{ width: '16px', height: '16px' }}
                        onError={(e) => {
                          e.target.style.visibility = 'hidden';
                          e.target.style.width = '0px';
                          e.target.style.height = '0px';
                          e.target.style.margin = '0px';
                        }}
                      />
                      <span>{safeGame.home}</span>
                    </div>
                  </td>
                  {bookLogos.map(book => {
                    const homeData = spreadsData.find(d => d.team === 'home' && d[`spreads_${book}`]);
                    const odds = homeData?.[`spreads_${book}`];
                    const bestBookPrice = homeData?.['spreads_bestBookPrice'];
                    const bestBookDiff = recalcEdgeOdds && homeData
                      ? recalculateEdge(homeData, book, 'spreads', allBookmakers, null)
                      : homeData?.['spreads_bestBookDiff'];
                    const cellStyle = odds ? getCellStyle(odds, bestBookPrice, bestBookDiff, bookLogos.length) : {};

                    return (
                      <td key={`spreads-${book}-home`} style={{...oddsCellStyle, ...cellStyle, textAlign: 'center', padding: '8px 4px', fontSize: '0.8rem'}}>
                        {homeData ? (
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{odds > 0 ? `+${odds}` : odds}</div>
                            <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{homeData.point > 0 ? `+${homeData.point}` : homeData.point}</div>
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </Table>
          </div>
        )}
        </div>
      </div>
    );
  }

  // Check if this is a basketball stat market with over/under
  const isBasketballStatMarket = ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_blocks', 'player_steals', 'player_points_rebounds_assists', 'player_points_rebounds', 'player_points_assists', 'player_rebounds_assists'].includes(selectedMarket);

  // For basketball stat markets, group all lines per player (like NFL yards markets)
  if (isBasketballStatMarket) {
    const playersByName = {};
    sortedData.forEach(player => {
      const key = player.name || player.playerId;
      if (!playersByName[key]) {
        playersByName[key] = {
          name: player.name,
          team: player.team,
          teamId: player.teamId,
          playerId: player.playerId,
          position: player.position,
          positionCode: player.positionCode,
          lines: []
        };
      }
      // Add this line/point value to the player's collection
      playersByName[key].lines.push(player);
    });

    // Group by team for display
    const playersByTeam = {};
    Object.values(playersByName).forEach(playerData => {
      let teamKey = 'unknown';
      let teamName = 'Unknown Team';
      let teamLogo = null;

      const player = playerData.lines[0]; // Use first line to determine team
      if (player.team === safeGame.away || (player.teamId && player.teamId === safeGame.awayTeamId)) {
        teamKey = 'away';
        teamName = safeGame.awayTeamFull || safeGame.away;
        teamLogo = safeGame.away;
      } else if (player.team === safeGame.home || (player.teamId && player.teamId === safeGame.homeTeamId)) {
        teamKey = 'home';
        teamName = safeGame.homeTeamFull || safeGame.home;
        teamLogo = safeGame.home;
      } else {
        teamKey = player.team || 'unknown';
        teamName = player.team || 'Unknown Team';
        teamLogo = player.team;
      }

      if (!playersByTeam[teamKey]) {
        playersByTeam[teamKey] = {
          name: teamName,
          logo: teamLogo,
          players: []
        };
      }

      playersByTeam[teamKey].players.push(playerData);
    });

    return (
      <div className="table-group">
        <GameHeader
          gameTime={gameTime}
          gameDate={gameDate}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={isMobile}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatBasketballLiveStatus}
          showScores={true}
            isLoading={showLoading}
        />

        {!(isMobile && isCollapsed) && (
        <>
        <div className="table-container">
          {Object.entries(playersByTeam).sort(([a], [b]) => {
            // Ensure away team comes before home team
            if (a === 'away') return -1;
            if (b === 'away') return 1;
            if (a === 'home') return 1;
            if (b === 'home') return -1;
            return a.localeCompare(b);
          }).map(([teamKey, teamData], teamIndex) => (
            <div key={teamKey} style={{ marginBottom: teamIndex < Object.keys(playersByTeam).length - 1 ? '24px' : '0' }}>
              <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed', minWidth: `${235 + bookLogos.length * 75}px` }}>
                <thead>
                  <tr>
                    <th style={{ width: '235px' }}>NAME</th>
                    {bookLogos.map(book => {
                      const bookLink = safeGame.sgoLinks?.bookmakers?.[book];
                      return (
                        <th key={`${selectedMarket}-${book}`} style={oddsCellStyle}>
                          {bookLink ? (
                            <a
                              href={bookLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-block',
                                touchAction: 'auto',
                                WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                                padding: '4px'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('Player props logo clicked:', book, bookLink);
                              }}
                              onTouchEnd={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <img
                                src={getBookLogoUrl(book)}
                                alt={`${book} logo`}
                                title={book}
                                style={{
                                  height: '20px',
                                  cursor: 'pointer',
                                  pointerEvents: 'auto',
                                  display: 'block'
                                }}
                              />
                            </a>
                          ) : (
                            <img
                              src={getBookLogoUrl(book)}
                              alt={`${book} logo`}
                              title={book}
                              style={{ height: '20px' }}
                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    console.log('BasketballDataTable rendering check:', {
                      selectedMarket,
                      isBinary: isBinaryMarket(selectedMarket),
                      nbaViewMode,
                      teamDataPlayers: teamData?.players?.length
                    });

                    // Best Line view - single row per player showing best edge for each book
                    // Binary markets use simple single-row view (no line values, just odds)
                    if (isBinaryMarket(selectedMarket)) {
                      console.log('Rendering BINARY market view for:', selectedMarket);
                      return teamData.players.map((playerData, index) => {
                        const line = playerData.lines && playerData.lines.length > 0 ? playerData.lines[0] : playerData;
                        return (
                          <tr key={`${playerData.name}-${index}`}>
                            <td style={{ textAlign: 'left', padding: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <img
                                    src={getTeamLogoUrl(teamData.logo)}
                                    alt={teamData.logo}
                                    style={{ width: '16px', height: '16px' }}
                                    onError={(e) => {
                                      e.target.style.visibility = 'hidden';
                                      e.target.style.width = '0px';
                                      e.target.style.height = '0px';
                                      e.target.style.margin = '0px';
                                    }}
                                  />
                                  <span
                                    style={{ cursor: 'pointer' }}
                                    onContextMenu={handleNameContextMenu(playerData)}
                                    onClick={() => {
                                      if (onPlayerClick) {
                                        onPlayerClick(
                                          safeGame.gameIDmlb,
                                          playerData.playerId,
                                          playerData.name,
                                          selectedMarket,
                                          safeGame.away,
                                          safeGame.home,
                                          playerData.team || playerData.teamId,
                                          playerData.positionCode || playerData.position
                                        );
                                      }
                                    }}
                                  >
                                    {formatPlayerName(playerData.name, playerData)}
                                  </span>
                                  {selectedMarket !== 'player_first_basket' && selectedMarket !== 'player_first_team_basket' && selectedMarket !== 'player_double_double' && selectedMarket !== 'player_triple_double' && renderPositionBadge(playerData, safeGame)}
                                </div>
                                {(selectedMarket === 'player_first_basket' || selectedMarket === 'player_first_team_basket' || selectedMarket === 'player_double_double' || selectedMarket === 'player_triple_double') ? (
                                  <span style={{ flexShrink: 0, marginLeft: '8px' }}>
                                    {renderPositionBadge(playerData, safeGame)}
                                  </span>
                                ) : (
                                  <small style={{
                                    color: '#6B7280',
                                    fontSize: '0.75rem',
                                    flexShrink: 0,
                                    marginLeft: '8px'
                                  }}>
                                    {line.point === 'yes' ? 'Yes' :
                                     line.point === 'no' ? 'No' :
                                     line.point !== null && line.point !== undefined ? `${line.point}` : ''}
                                  </small>
                                )}
                              </div>
                            </td>
                            {bookLogos.map(book => {
                              // For binary markets, use direct field name (no _over suffix)
                              const odds = line[`${selectedMarket}_${book}`];
                              const bestBookPrice = line[`${selectedMarket}_bestBookPrice`];
                              const bestBook = line[`${selectedMarket}_bestBook`];
                              const bestBookDiff = recalcEdgeOdds
                                ? getEdgeForPlayerBook(line, book, selectedMarket, allBookmakers)
                                : (odds === bestBookPrice ? (line[`${selectedMarket}_bestBookDiff`] || 0) : 0);
                              const bookCount = bookLogos.length;
                              // Don't show edges when multiple books have the same best price (data error)
                              const displayDiff = bestBook === 'multi' ? 0 : bestBookDiff;
                              const cellStyle = getCellStyle(odds, bestBookPrice, displayDiff, bookCount);
                              const tooltipText = getTooltipText(odds, bestBookPrice, displayDiff, bookCount);

                              // Debug for Zubac or -245
                              if (playerData.name?.includes('Zubac') || odds === -245) {
                                console.log('Binary market debug (first path):', {
                                  player: playerData.name,
                                  book,
                                  odds,
                                  bestBookPrice,
                                  bestBookDiff,
                                  bookCount,
                                  cellStyle,
                                  market: selectedMarket,
                                  apiBestBookDiff: line[`${selectedMarket}_bestBookDiff`]
                                });
                              }

                              return (
                                <td
                                  key={`${selectedMarket}-${book}`}
                                  style={{...oddsCellStyle, ...cellStyle, cursor: 'pointer', opacity: line[`${selectedMarket}_${book}_isAvailable`] === false ? 0.25 : undefined}}
                                  onClick={() => {
                                    if (odds && onOddsClick) {
                                      onOddsClick(safeGame.gameIDmlb, playerData.playerId, playerData.name, selectedMarket, book, odds, safeGame.away, safeGame.home, playerData.team, playerData.position);
                                    }
                                  }}
                                  onContextMenu={(e) => {
                                    if (odds && onOddsContextMenu) {
                                      onOddsContextMenu(e, safeGame.gameIDmlb, playerData.playerId, playerData.name, selectedMarket, book, odds, safeGame.away, safeGame.home, playerData.team, playerData.position);
                                    }
                                  }}
                                  title={tooltipText || undefined}
                                >
                                  {odds ? (
                                    <div style={{ fontSize: '0.9rem', fontWeight: cellStyle.fontWeight || 'bold' }}>
                                      {odds > 0 ? `+${odds}` : odds}
                                    </div>
                                  ) : null}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      });
                    }

                    // Best Line view - single row per player showing best edge for each book
                    if (nbaViewMode === 'bestline') {
                      return teamData.players.map((playerData, index) => {
                        // Debug logging only for Caris LeVert
                        if (playerData.name === 'Caris LeVert') {
                          console.log('=== CARIS LEVERT DATA ===', {
                            playerName: playerData.name,
                            lines: playerData.lines,
                            numLines: playerData.lines?.length
                          });
                        }
                        return (
                          <tr key={`${playerData.name}-${index}`}>
                            <td style={{ textAlign: 'left', padding: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <img
                                    src={getTeamLogoUrl(teamData.logo)}
                                    alt={teamData.logo}
                                    style={{ width: '16px', height: '16px' }}
                                    onError={(e) => {
                                      e.target.style.visibility = 'hidden';
                                      e.target.style.width = '0px';
                                      e.target.style.height = '0px';
                                      e.target.style.margin = '0px';
                                    }}
                                  />
                                  <span
                                    style={{ cursor: 'pointer' }}
                                    onContextMenu={handleNameContextMenu(playerData)}
                                    onClick={() => {
                                      if (onPlayerClick) {
                                        onPlayerClick(
                                          safeGame.gameIDmlb,
                                          playerData.playerId,
                                          playerData.name,
                                          selectedMarket,
                                          safeGame.away,
                                          safeGame.home,
                                          playerData.team || playerData.teamId,
                                          playerData.positionCode || playerData.position
                                        );
                                      }
                                    }}
                                  >
                                    {formatPlayerName(playerData.name, playerData)}
                                  </span>
                                </div>
                                <span style={{ flexShrink: 0, marginLeft: '8px' }}>
                                  {renderPositionBadge(playerData, safeGame)}
                                </span>
                              </div>
                            </td>
                            {bookLogos.map(book => {
                              // Show skeleton loading only while data is being fetched
                              if (isLoading) {
                                return (
                                  <td key={`${selectedMarket}-${book}-bestline`} style={{...oddsCellStyle, textAlign: 'center'}}>
                                    <div className="skeleton-odds"></div>
                                  </td>
                                );
                              }

                              // Debug for Caris LeVert + DraftKings only
                              if (playerData.name === 'Caris LeVert' && book === 'draftkings') {
                                console.log('Calling getBestLineByEdge for Caris/DK:', { book, market: selectedMarket });
                              }

                              // Pass all lines for this player to find the best edge
                              const bestLine = getBestLineByEdge(playerData.lines, book, selectedMarket);

                              if (playerData.name === 'Caris LeVert' && book === 'draftkings') {
                                console.log('getBestLineByEdge returned for Caris/DK:', bestLine);
                              }

                              if (!bestLine) {
                                return (
                                  <td key={`${selectedMarket}-${book}-bestline`} style={{...oddsCellStyle, textAlign: 'center'}}>
                                    <span style={{ color: '#6B7280', fontSize: '0.9rem' }}>-</span>
                                  </td>
                                );
                              }

                              const { odds, point, side, bestBookPrice, bestBookDiff } = bestLine;

                              // Validate that we have valid odds
                              if (odds === null || odds === undefined) {
                                return (
                                  <td key={`${selectedMarket}-${book}-bestline`} style={{...oddsCellStyle, textAlign: 'center'}}>
                                    <span style={{ color: '#6B7280', fontSize: '0.9rem' }}>-</span>
                                  </td>
                                );
                              }

                              // Apply tier styling using the edge data from API
                              const cellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, bookLogos.length);
                              const tooltipText = getTooltipText(odds, bestBookPrice, bestBookDiff, bookLogos.length);

                              return (
                                <td
                                  key={`${selectedMarket}-${book}-bestline`}
                                  className={getCellClassName(safeGame.gameIDmlb, playerData.playerId, selectedMarket, book, point, side)}
                                  style={{...oddsCellStyle, cursor: 'pointer'}}
                                  onClick={() => {
                                    if (onOddsClick) {
                                      onOddsClick(safeGame.gameIDmlb, playerData.playerId, playerData.name, selectedMarket, book, odds, safeGame.away, safeGame.home, playerData.team, playerData.position, point, side);
                                    }
                                  }}
                                  onContextMenu={(e) => {
                                    if (onOddsContextMenu) {
                                      onOddsContextMenu(e, safeGame.gameIDmlb, playerData.playerId, playerData.name, selectedMarket, book, odds, safeGame.away, safeGame.home, playerData.team, playerData.position, point, side);
                                    }
                                  }}
                                  title={tooltipText || undefined}
                                >
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                                    <span style={{ fontSize: '0.7rem' }}>
                                      {point === 'yes' || point === 'no' ? '' : (side === 'over' ? `o${point}` : `u${point}`)}
                                    </span>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                      {odds > 0 ? `+${odds}` : odds}
                                    </span>
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      });
                    }

                    // O/U view - two rows per player
                    // For each book, evaluate individually to find the best main line
                    let isEven = true;

                    return teamData.players.map((playerData, index) => {
                      const bgColor = isEven ? 'rgb(26, 26, 26)' : 'rgb(45, 45, 45)';
                      isEven = !isEven;

                      // Build a map of book -> main line data for this player
                      const bookMainLines = {};
                      const allLines = playerData.lines || [playerData];

                      bookLogos.forEach(book => {
                        // Find all lines where this book has isAlternate=false
                        const mainLineEntries = allLines.filter(line => {
                          const hasOver = line[`${selectedMarket}_${book}_over`] !== null &&
                                         line[`${selectedMarket}_${book}_over`] !== undefined;
                          const hasUnder = line[`${selectedMarket}_${book}_under`] !== null &&
                                          line[`${selectedMarket}_${book}_under`] !== undefined;
                          const isMainLine = line[`${selectedMarket}_${book}_isAlternate`] === false;

                          // Debug for Suggs + bet365
                          if (playerData.name?.includes('Suggs') && book === 'bet365') {
                            console.log('Bet365 filter check:', {
                              point: line.point,
                              hasOver,
                              hasUnder,
                              isMainLine,
                              isAlternateValue: line[`${selectedMarket}_${book}_isAlternate`],
                              passes: (hasOver || hasUnder) && isMainLine
                            });
                          }

                          return (hasOver || hasUnder) && isMainLine;
                        });

                        // Debug for Suggs + bet365
                        if (playerData.name?.includes('Suggs') && book === 'bet365') {
                          console.log('Bet365 main line entries found:', mainLineEntries.length, {
                            allLinesCount: allLines.length,
                            entries: mainLineEntries.map(e => ({ point: e.point, isAlt: e[`${selectedMarket}_${book}_isAlternate`] }))
                          });
                        }

                        if (mainLineEntries.length > 0) {
                          if (mainLineEntries.length === 1) {
                            bookMainLines[book] = mainLineEntries[0];
                          } else {
                            // Multiple main lines - prefer one where this book has best edge
                            let bestEntry = mainLineEntries[0];
                            let bestEdge = 0;
                            let bestCoverage = 0;

                            mainLineEntries.forEach(line => {
                              const bestBook = line[`${selectedMarket}_bestBook`];
                              const edge = line[`${selectedMarket}_bestBookDiff`] || 0;
                              const coverage = bookLogos.filter(b =>
                                line[`${selectedMarket}_${b}_over`] !== null &&
                                line[`${selectedMarket}_${b}_over`] !== undefined
                              ).length;

                              if (bestBook === book && edge > bestEdge) {
                                bestEdge = edge;
                                bestEntry = line;
                              } else if (bestEdge === 0 && coverage > bestCoverage) {
                                bestCoverage = coverage;
                                bestEntry = line;
                              }
                            });

                            bookMainLines[book] = bestEntry;
                          }
                        }
                      });

                      return (
                        <React.Fragment key={`${playerData.name}-${index}`}>
                          {/* Over row */}
                          <tr style={{ backgroundColor: bgColor }}>
                            <td rowSpan={2} style={{
                              verticalAlign: 'middle',
                              textAlign: 'left',
                              padding: '8px',
                              backgroundColor: bgColor
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <img
                                    src={getTeamLogoUrl(teamData.logo)}
                                    alt={teamData.logo}
                                    style={{ width: '16px', height: '16px' }}
                                    onError={(e) => {
                                      e.target.style.visibility = 'hidden';
                                      e.target.style.width = '0px';
                                      e.target.style.height = '0px';
                                      e.target.style.margin = '0px';
                                    }}
                                  />
                                  <span
                                    style={{ cursor: 'pointer' }}
                                    onContextMenu={handleNameContextMenu(playerData)}
                                    onClick={() => {
                                      if (onPlayerClick) {
                                        onPlayerClick(
                                          safeGame.gameIDmlb,
                                          playerData.playerId,
                                          playerData.name,
                                          selectedMarket,
                                          safeGame.away,
                                          safeGame.home,
                                          playerData.team || playerData.teamId,
                                          playerData.positionCode || playerData.position
                                        );
                                      }
                                    }}
                                  >
                                    {formatPlayerName(playerData.name, playerData)}
                                  </span>
                                </div>
                                <span style={{ flexShrink: 0, marginLeft: '8px' }}>
                                  {renderPositionBadge(playerData, safeGame)}
                                </span>
                              </div>
                            </td>
                            {bookLogos.map(book => {
                              // Show skeleton loading while data is being fetched
                              if (isLoading) {
                                return (
                                  <td key={`${selectedMarket}-${book}-over`} style={{ textAlign: 'center', padding: '4px', borderBottom: `1px solid ${bgColor}` }}>
                                    <div className="skeleton-odds"></div>
                                  </td>
                                );
                              }

                              // Use the main line entry for this book, or fall back to first line
                              const lineWithOdds = bookMainLines[book] || allLines[0];

                              // For binary markets, use direct field name (no _over suffix)
                              const overOdds = isBinaryMarket(selectedMarket)
                                ? lineWithOdds[`${selectedMarket}_${book}`]
                                : lineWithOdds[`${selectedMarket}_${book}_over`];
                              const point = lineWithOdds[`${selectedMarket}_${book}_point`] || lineWithOdds.point;
                              const bestBookPrice = lineWithOdds[`${selectedMarket}_bestBookPrice`];
                              // Edge: recalculate client-side or use server-provided bestBookDiff
                              const bestBookDiff = recalcEdgeOdds
                                ? getEdgeForPlayerBook(lineWithOdds, book, selectedMarket, allBookmakers)
                                : (overOdds === bestBookPrice ? (lineWithOdds[`${selectedMarket}_bestBookDiff`] || 0) : 0);
                              const bookCount = bookLogos.length;

                              // Only apply styling if odds are > -200
                              const cellStyle = (overOdds && overOdds > -200) ? getCellStyle(overOdds, bestBookPrice, bestBookDiff, bookCount) : {};
                              const tooltipText = (overOdds && overOdds > -200) ? getTooltipText(overOdds, bestBookPrice, bestBookDiff, bookCount) : null;

                              return (
                                <td
                                  key={`${selectedMarket}-${book}-over`}
                                  className={getCellClassName(safeGame.gameIDmlb, playerData.playerId, selectedMarket, book, point, 'over')}
                                  onClick={() => {
                                    if (overOdds && overOdds > -200 && onOddsClick) {
                                      onOddsClick(safeGame.gameIDmlb, playerData.playerId, playerData.name, selectedMarket, book, overOdds, safeGame.away, safeGame.home, playerData.team, playerData.position, point, 'over');
                                    }
                                  }}
                                  onContextMenu={(e) => {
                                    if (overOdds && overOdds > -200 && onOddsContextMenu) {
                                      onOddsContextMenu(e, safeGame.gameIDmlb, playerData.playerId, playerData.name, selectedMarket, book, overOdds, safeGame.away, safeGame.home, playerData.team, playerData.position, point, 'over');
                                    }
                                  }}
                                  title={tooltipText || undefined}
                                  style={{
                                    textAlign: 'center',
                                    padding: '4px 4px 0px 4px',
                                    fontSize: '0.85rem',
                                    cursor: (overOdds && overOdds > -200) ? 'pointer' : 'default',
                                    borderBottom: `1px solid ${bgColor}`,
                                    opacity: lineWithOdds[`${selectedMarket}_${book}_over_isAvailable`] === false ? 0.25 : undefined
                                  }}
                                >
                                  {(overOdds && overOdds > -200) ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                                      <div style={{ fontWeight: 'bold' }}>{overOdds > 0 ? `+${overOdds}` : overOdds}</div>
                                      <div style={{ fontSize: '0.75rem', color: cellStyle.backgroundColor ? 'inherit' : '#6c757d' }}>o{point}</div>
                                    </div>
                                  ) : null}
                                </td>
                              );
                            })}
                          </tr>
                          {/* Under row */}
                          <tr style={{ backgroundColor: bgColor }}>
                            {bookLogos.map(book => {
                              // Show skeleton loading while data is being fetched
                              if (isLoading) {
                                return (
                                  <td key={`${selectedMarket}-${book}-under`} style={{ textAlign: 'center', padding: '4px' }}>
                                    <div className="skeleton-odds"></div>
                                  </td>
                                );
                              }

                              // Use the SAME entry that was selected for the over row
                              const lineWithOdds = bookMainLines[book] || allLines[0];

                              const underOdds = lineWithOdds[`${selectedMarket}_${book}_under`];
                              const point = lineWithOdds[`${selectedMarket}_${book}_point`] || lineWithOdds.point;
                              const bestBookPrice = lineWithOdds[`${selectedMarket}_bestBookPrice`];
                              // Edge: recalculate client-side or use server-provided bestBookDiff
                              const bestBookDiff = recalcEdgeOdds
                                ? getEdgeForPlayerBook(lineWithOdds, book, selectedMarket, allBookmakers)
                                : (underOdds === bestBookPrice ? (lineWithOdds[`${selectedMarket}_bestBookDiff`] || 0) : 0);
                              const bookCount = bookLogos.length;

                              const cellStyle = getCellStyle(underOdds, bestBookPrice, bestBookDiff, bookCount);
                              const tooltipText = getTooltipText(underOdds, bestBookPrice, bestBookDiff, bookCount);

                              return (
                                <td
                                  key={`${selectedMarket}-${book}-under`}
                                  className={getCellClassName(safeGame.gameIDmlb, playerData.playerId, selectedMarket, book, point, 'under')}
                                  onClick={() => {
                                    if (underOdds && onOddsClick) {
                                      onOddsClick(safeGame.gameIDmlb, playerData.playerId, playerData.name, selectedMarket, book, underOdds, safeGame.away, safeGame.home, playerData.team, playerData.position, point, 'under');
                                    }
                                  }}
                                  onContextMenu={(e) => {
                                    if (underOdds && onOddsContextMenu) {
                                      onOddsContextMenu(e, safeGame.gameIDmlb, playerData.playerId, playerData.name, selectedMarket, book, underOdds, safeGame.away, safeGame.home, playerData.team, playerData.position, point, 'under');
                                    }
                                  }}
                                  title={tooltipText || undefined}
                                  style={{
                                    textAlign: 'center',
                                    padding: '0px 4px 4px 4px',
                                    fontSize: '0.85rem',
                                    cursor: underOdds ? 'pointer' : 'default',
                                    opacity: lineWithOdds[`${selectedMarket}_${book}_under_isAvailable`] === false ? 0.25 : undefined
                                  }}
                                >
                                  {underOdds ? (
                                    <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                                      <div style={{ fontSize: '0.75rem', color: cellStyle.backgroundColor ? 'inherit' : '#6c757d' }}>u{point}</div>
                                      <div style={{ fontWeight: 'bold' }}>{underOdds > 0 ? `+${underOdds}` : underOdds}</div>
                                    </div>
                                  ) : null}
                                </td>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      );
                    });
                  })()}
                </tbody>
              </Table>
            </div>
          ))}
        </div>
        </>
        )}
      </div>
    );
  }

  // Original code for non-stat markets
  const playersByTeam = {};
  sortedData.forEach(player => {
    // Determine which team the player belongs to
    let teamKey = 'unknown';
    let teamName = 'Unknown Team';
    let teamLogo = null;
    
    // Try to determine team based on various fields
    if (player.team === safeGame.away || (player.teamId && player.teamId === safeGame.awayTeamId)) {
      teamKey = 'away';
      teamName = safeGame.awayTeamFull || safeGame.away;
      teamLogo = safeGame.away; // Store abbreviation, not URL
    } else if (player.team === safeGame.home || (player.teamId && player.teamId === safeGame.homeTeamId)) {
      teamKey = 'home';
      teamName = safeGame.homeTeamFull || safeGame.home;
      teamLogo = safeGame.home; // Store abbreviation, not URL
    } else {
      // Fallback - try to guess based on name or other indicators
      teamKey = player.team || 'unknown';
      teamName = player.team || 'Unknown Team';
      teamLogo = player.team; // Store abbreviation, not URL
    }
    
    if (!playersByTeam[teamKey]) {
      playersByTeam[teamKey] = {
        name: teamName,
        logo: teamLogo,
        players: []
      };
    }
    
    playersByTeam[teamKey].players.push(player);
  });
  
  return (
    <div className="table-group">
      <GameHeader
        gameTime={gameTime}
          gameDate={gameDate}
        gameStatus={gameStatus}
        game={game}
        getTeamLogoUrl={getTeamLogoUrl}
        isMobile={isMobile}
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
        formatLiveStatus={formatBasketballLiveStatus}
        showScores={true}
            isLoading={showLoading}
      />

      {!(isMobile && isCollapsed) && (
      <>
      <div className="table-container">
        {Object.entries(playersByTeam).sort(([a], [b]) => {
          // Ensure away team comes before home team
          if (a === 'away') return -1;
          if (b === 'away') return 1;
          if (a === 'home') return 1;
          if (b === 'home') return -1;
          return a.localeCompare(b);
        }).map(([teamKey, teamData], teamIndex) => (
        <div key={teamKey} style={{ marginBottom: teamIndex < Object.keys(playersByTeam).length - 1 ? '24px' : '0' }}>
          <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed', minWidth: `${235 + bookLogos.length * 75}px` }}>
            <thead>
              <tr>
                <th style={{ width: '235px' }}>NAME</th>
                {bookLogos.map(book => {
                  const bookLink = safeGame.sgoLinks?.bookmakers?.[book];
                  return (
                    <th key={`${selectedMarket}-${book}`} style={oddsCellStyle}>
                      {bookLink ? (
                        <a
                          href={bookLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block',
                            touchAction: 'auto',
                            WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                            padding: '4px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('Book logo clicked:', book, bookLink);
                          }}
                          onTouchEnd={(e) => {
                            e.stopPropagation();
                            console.log('Book logo touched:', book, bookLink);
                          }}
                        >
                          <img
                            src={getBookLogoUrl(book)}
                            alt={`${book} logo`}
                            title={book}
                            style={{
                              height: '20px',
                              cursor: 'pointer',
                              pointerEvents: 'auto',
                              display: 'block'
                            }}
                          />
                        </a>
                      ) : (
                        <img
                          src={getBookLogoUrl(book)}
                          alt={`${book} logo`}
                          title={book}
                          style={{ height: '20px' }}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {(() => {
                console.log('Basketball market check (second path):', {
                  selectedMarket,
                  isBinary: isBinaryMarket(selectedMarket),
                  playersLength: teamData.players.length
                });

                // Binary markets - simple single row per player with edge calculations
                if (isBinaryMarket(selectedMarket)) {
                  console.log('Rendering BINARY market (second path) for:', selectedMarket);
                  return teamData.players.map((player, index) => {
                    return (
                      <tr key={`${player.name}-${index}`}>
                        <td style={{ textAlign: 'left', padding: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <img
                                src={getTeamLogoUrl(teamData.logo)}
                                alt={teamData.logo}
                                style={{ width: '16px', height: '16px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                  e.target.style.margin = '0px';
                                }}
                              />
                              <span
                                style={{ cursor: 'pointer' }}
                                onContextMenu={handleNameContextMenu(player)}
                                onClick={() => {
                                  if (onPlayerClick) {
                                    onPlayerClick(
                                      safeGame.gameIDmlb,
                                      player.playerId,
                                      player.name,
                                      selectedMarket,
                                      safeGame.away,
                                      safeGame.home,
                                      player.team || player.teamId,
                                      player.positionCode || player.position
                                    );
                                  }
                                }}
                              >
                                {formatPlayerName(player.name, player)}
                              </span>
                              {selectedMarket !== 'player_first_basket' && selectedMarket !== 'player_first_team_basket' && selectedMarket !== 'player_double_double' && selectedMarket !== 'player_triple_double' && renderPositionBadge(player, safeGame)}
                            </div>
                            {(selectedMarket === 'player_first_basket' || selectedMarket === 'player_first_team_basket' || selectedMarket === 'player_double_double' || selectedMarket === 'player_triple_double') ? (
                              <span style={{ flexShrink: 0, marginLeft: '8px' }}>
                                {renderPositionBadge(player, safeGame)}
                              </span>
                            ) : (
                              <small style={{
                                color: '#6B7280',
                                fontSize: '0.75rem',
                                flexShrink: 0,
                                marginLeft: '8px'
                              }}>
                                {player.point === 'yes' ? 'Yes' :
                                 player.point === 'no' ? 'No' :
                                 player.point !== null && player.point !== undefined ? `${player.point}` : ''}
                              </small>
                            )}
                          </div>
                        </td>
                        {bookLogos.map(book => {
                          // For binary markets, use direct field name (no _over suffix)
                          const odds = player[`${selectedMarket}_${book}`];
                          const bestBookPrice = player[`${selectedMarket}_bestBookPrice`];
                          const bestBookDiff = recalcEdgeOdds
                            ? getEdgeForPlayerBook(player, book, selectedMarket, allBookmakers)
                            : (odds === bestBookPrice ? (player[`${selectedMarket}_bestBookDiff`] || 0) : 0);
                          const bookCount = bookLogos.length;
                          const cellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, bookCount);
                          const tooltipText = getTooltipText(odds, bestBookPrice, bestBookDiff, bookCount);

                          // Debug for Zubac or -245
                          if (player.name?.includes('Zubac') || odds === -245) {
                            console.log('Binary market debug (second path):', {
                              player: player.name,
                              book,
                              odds,
                              bestBookPrice,
                              bestBookDiff,
                              bookCount,
                              cellStyle,
                              market: selectedMarket,
                              apiBestBookDiff: player[`${selectedMarket}_bestBookDiff`]
                            });
                          }

                          return (
                            <td
                              key={`${selectedMarket}-${book}`}
                              style={{...oddsCellStyle, ...cellStyle, cursor: 'pointer', opacity: player[`${selectedMarket}_${book}_isAvailable`] === false ? 0.25 : undefined}}
                              onClick={() => {
                                if (odds && onOddsClick) {
                                  onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position);
                                }
                              }}
                              onContextMenu={(e) => {
                                if (odds && onOddsContextMenu) {
                                  onOddsContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position);
                                }
                              }}
                              title={tooltipText || undefined}
                            >
                              {odds ? (
                                <div style={{ fontSize: '0.9rem', fontWeight: cellStyle.fontWeight || 'normal' }}>
                                  {odds > 0 ? `+${odds}` : odds}
                                </div>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  });
                }

                // Check if this is a basketball stat market that has over/under
                // Exclude binary markets from O/U view (they should use the binary rendering path)
                const isBasketballStatMarket = ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_blocks', 'player_steals', 'player_points_rebounds_assists', 'player_points_rebounds', 'player_points_assists', 'player_rebounds_assists'].includes(selectedMarket);
                const hasOverUnder = teamData.players.length > 0 && teamData.players[0][`${selectedMarket}_${bookLogos[0]}_over`] !== undefined;

                if (isBasketballStatMarket && hasOverUnder && !isBinaryMarket(selectedMarket)) {
                  // Group players by name - just need one entry per player for basic info
                  // The O/U view logic will find the correct book-specific main lines
                  const playersByName = {};
                  teamData.players.forEach(player => {
                    // Store first entry for each player name - we only need basic player info
                    if (!playersByName[player.name]) {
                      playersByName[player.name] = player;
                    }
                  });

                  // Best Line view - single row per player showing best edge for each book
                  if (nbaViewMode === 'bestline') {
                    console.log('BasketballDataTable: Rendering Best Line view', { nbaViewMode, sport: safeGame.sport });
                    return Object.values(playersByName).map((player, playerIndex) => {
                      const edgeData = getBestLineEdge(player, selectedMarket, allBookmakers);

                      return (
                        <tr key={`${player.name}-${playerIndex}`}>
                          <td style={{ textAlign: 'left', padding: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <img
                                  src={getTeamLogoUrl(teamData.logo)}
                                  alt={teamData.logo}
                                  style={{ width: '16px', height: '16px' }}
                                  onError={(e) => {
                                    e.target.style.visibility = 'hidden';
                                    e.target.style.width = '0px';
                                    e.target.style.height = '0px';
                                    e.target.style.margin = '0px';
                                  }}
                                />
                                <span
                                  onContextMenu={handleNameContextMenu(player)}
                                  onClick={() => {
                                    if (onPlayerClick) {
                                      onPlayerClick(
                                        safeGame.gameIDmlb,
                                        player.playerId,
                                        player.name,
                                        selectedMarket,
                                        safeGame.away,
                                        safeGame.home,
                                        player.team || player.teamId,
                                        player.position || player.positionCode || 'N/A'
                                      );
                                    }
                                  }}
                                  style={{
                                    cursor: onPlayerClick ? 'pointer' : 'default'
                                  }}
                                >
                                  {formatPlayerName(player.name, player)}
                                </span>
                              </div>
                              <span style={{ flexShrink: 0, marginLeft: '8px' }}>
                                {renderPositionBadge(player, safeGame)}
                              </span>
                            </div>
                          </td>
                          {bookLogos.map(book => {
                            const bestLine = getBestLine(player, book, selectedMarket);
                            if (!bestLine) {
                              return (
                                <td key={`${selectedMarket}-${book}-bestline`} style={oddsCellStyle}>
                                  <span style={{ color: '#9CA3AF' }}>-</span>
                                </td>
                              );
                            }

                            const { odds, point, side } = bestLine;

                            // Calculate tier styling
                            let cellStyle = { ...oddsCellStyle };
                            if (edgeData && odds === edgeData.bestOdds && edgeData.bestOddsCount === 1 && allBookmakers.length > 1) {
                              const diff = edgeData.diff;
                              if (diff >= 0.25) {
                                // Tier 1: Green with yellow text
                                Object.assign(cellStyle, { backgroundColor: 'green', color: 'yellow', fontWeight: 'bold' });
                              } else if (diff >= 0.15) {
                                // Tier 2: Amber with dark text
                                Object.assign(cellStyle, { backgroundColor: '#F59E0B', color: '#1E40AF', fontWeight: 'bold' });
                              } else if (diff >= 0.05) {
                                // Tier 3: Blue
                                Object.assign(cellStyle, { backgroundColor: '#3B82F6', color: 'white', fontWeight: 'bold' });
                              } else {
                                // Best odds but below tier threshold
                                Object.assign(cellStyle, { color: '#F59E0B', fontWeight: 'bold' });
                              }
                            }

                            return (
                              <td
                                key={`${selectedMarket}-${book}-bestline`}
                                className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, point, side)}
                                style={cellStyle}
                                onClick={() => onOddsClick && onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position, point, side)}
                                onContextMenu={(e) => onOddsContextMenu && onOddsContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position, point, side)}
                              >
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                  <span style={{ fontSize: '0.7rem', color: cellStyle.backgroundColor ? cellStyle.color : '#6B7280' }}>
                                    {point === 'yes' || point === 'no' ? '' : (side === 'over' ? `o${point}` : `u${point}`)}
                                  </span>
                                  <span style={{ fontSize: '0.75rem', fontWeight: cellStyle.fontWeight || 'normal' }}>
                                    {odds > 0 ? `+${odds}` : odds}
                                  </span>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    });
                  }

                  // O/U view - two rows per player
                  // For each book, evaluate individually to find the best main line
                  console.log('O/U View - Total players:', Object.keys(playersByName).length, 'First 5:', Object.keys(playersByName).slice(0, 5));

                  return Object.values(playersByName).map((player, playerIndex) => {
                    // Build a map of book -> main line data for this player
                    const bookMainLines = {};
                    const allEntriesForPlayer = teamData.players.filter(p => p.name === player.name);

                    // Debug first player
                    if (playerIndex === 0) {
                      console.log('O/U View - First player:', player.name, {
                        allEntriesCount: allEntriesForPlayer.length,
                        bookLogos,
                        hasFanduelOver: allEntriesForPlayer.some(e => e.player_points_fanduel_over !== null && e.player_points_fanduel_over !== undefined),
                        hasFanduelMainLine: allEntriesForPlayer.some(e => e.player_points_fanduel_isAlternate === false)
                      });
                    }

                    bookLogos.forEach(book => {
                      // Find all entries where this book has isAlternate=false
                      const mainLineEntries = allEntriesForPlayer.filter(entry => {
                        const hasOver = entry[`${selectedMarket}_${book}_over`] !== null &&
                                       entry[`${selectedMarket}_${book}_over`] !== undefined;
                        const hasUnder = entry[`${selectedMarket}_${book}_under`] !== null &&
                                        entry[`${selectedMarket}_${book}_under`] !== undefined;
                        const isMainLine = entry[`${selectedMarket}_${book}_isAlternate`] === false;
                        return (hasOver || hasUnder) && isMainLine;
                      });

                      if (mainLineEntries.length === 0) {
                        // Debug: Log when FanDuel has no main line entries
                        if (book === 'fanduel' && player.name?.includes('Suggs')) {
                          console.log('FanDuel O/U View - No main line entries found for:', player.name, {
                            allEntriesCount: allEntriesForPlayer.length,
                            sampleEntry: allEntriesForPlayer[0],
                            sampleKeys: allEntriesForPlayer[0] ? Object.keys(allEntriesForPlayer[0]).filter(k => k.includes('fanduel')) : []
                          });
                        }
                        return;
                      }

                      if (mainLineEntries.length === 1) {
                        bookMainLines[book] = mainLineEntries[0];
                        // Debug: Log FanDuel selection
                        if (book === 'fanduel' && player.name?.includes('Suggs')) {
                          console.log('FanDuel O/U View - Single main line found for:', player.name, {
                            point: mainLineEntries[0].point,
                            over: mainLineEntries[0].player_points_fanduel_over,
                            under: mainLineEntries[0].player_points_fanduel_under,
                            isAlternate: mainLineEntries[0].player_points_fanduel_isAlternate
                          });
                        }
                      } else {
                        // Multiple main lines for this book - pick best one
                        let bestEntry = mainLineEntries[0];
                        let bestEdge = 0;
                        let bestCoverage = 0;

                        mainLineEntries.forEach(entry => {
                          // Check if this book has the best edge on this entry
                          const bestBook = entry[`${selectedMarket}_bestBook`];
                          const edge = entry[`${selectedMarket}_bestBookDiff`] || 0;

                          // Count how many other books have odds on this entry
                          const coverage = bookLogos.filter(b =>
                            entry[`${selectedMarket}_${b}_over`] !== null &&
                            entry[`${selectedMarket}_${b}_over`] !== undefined
                          ).length;

                          // Prefer entry where this book has an edge
                          if (bestBook === book && edge > bestEdge) {
                            bestEdge = edge;
                            bestEntry = entry;
                          }
                          // If no edge found yet, prefer entry with most book coverage
                          else if (bestEdge === 0 && coverage > bestCoverage) {
                            bestCoverage = coverage;
                            bestEntry = entry;
                          }
                        });

                        bookMainLines[book] = bestEntry;
                      }
                    });

                    return (
                      <React.Fragment key={`${player.name}-${playerIndex}`}>
                        {/* Over row */}
                        <tr>
                          <td rowSpan={2} style={{ textAlign: 'left', padding: '8px', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <img
                                  src={getTeamLogoUrl(teamData.logo)}
                                  alt={teamData.logo}
                                  style={{ width: '16px', height: '16px' }}
                                  onError={(e) => {
                                    e.target.style.visibility = 'hidden';
                                    e.target.style.width = '0px';
                                    e.target.style.height = '0px';
                                    e.target.style.margin = '0px';
                                  }}
                                />
                                <span
                                  onContextMenu={handleNameContextMenu(player)}
                                  onClick={() => {
                                    if (onPlayerClick) {
                                      onPlayerClick(
                                        safeGame.gameIDmlb,
                                        player.playerId,
                                        player.name,
                                        selectedMarket,
                                        safeGame.away,
                                        safeGame.home,
                                        player.team || player.teamId,
                                        player.position || player.positionCode || 'N/A'
                                      );
                                    }
                                  }}
                                  style={{
                                    cursor: onPlayerClick ? 'pointer' : 'default'
                                  }}
                                >
                                  {formatPlayerName(player.name, player)}
                                </span>
                              </div>
                              <span style={{ flexShrink: 0, marginLeft: '8px' }}>
                                {renderPositionBadge(player, safeGame)}
                              </span>
                            </div>
                          </td>
                          {bookLogos.map(book => {
                            // Use the main line entry for this book
                            const bookEntry = bookMainLines[book] || player;
                            const overOdds = bookEntry[`${selectedMarket}_${book}_over`];
                            const bookPoint = bookEntry[`${selectedMarket}_${book}_point`] || bookEntry.point;
                            return (
                              <td
                                key={`${selectedMarket}-${book}-over`}
                                className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, bookPoint, 'over')}
                                style={{...oddsCellStyle, opacity: bookEntry[`${selectedMarket}_${book}_over_isAvailable`] === false ? 0.25 : undefined}}
                                onClick={() => onOddsClick && overOdds && onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, overOdds, safeGame.away, safeGame.home, player.team, player.position, bookPoint, 'over')}
                                onContextMenu={(e) => onOddsContextMenu && overOdds && onOddsContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, overOdds, safeGame.away, safeGame.home, player.team, player.position, bookPoint, 'over')}
                              >
                                {overOdds !== null && overOdds !== undefined ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                      {overOdds > 0 ? `+${overOdds}` : overOdds}
                                    </span>
                                    <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                                      o{bookPoint}
                                    </span>
                                  </div>
                                ) : (
                                  <span style={{ color: '#9CA3AF' }}>-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                        {/* Under row */}
                        <tr>
                          {bookLogos.map(book => {
                            // Use the main line entry for this book
                            const bookEntry = bookMainLines[book] || player;
                            const underOdds = bookEntry[`${selectedMarket}_${book}_under`];
                            const bookPoint = bookEntry[`${selectedMarket}_${book}_point`] || bookEntry.point;
                            return (
                              <td
                                key={`${selectedMarket}-${book}-under`}
                                className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, bookPoint, 'under')}
                                style={{...oddsCellStyle, opacity: bookEntry[`${selectedMarket}_${book}_under_isAvailable`] === false ? 0.25 : undefined}}
                                onClick={() => onOddsClick && underOdds && onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, underOdds, safeGame.away, safeGame.home, player.team, player.position, bookPoint, 'under')}
                                onContextMenu={(e) => onOddsContextMenu && underOdds && onOddsContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, underOdds, safeGame.away, safeGame.home, player.team, player.position, bookPoint, 'under')}
                              >
                                {underOdds !== null && underOdds !== undefined ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                                    <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                                      u{bookPoint}
                                    </span>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                      {underOdds > 0 ? `+${underOdds}` : underOdds}
                                    </span>
                                  </div>
                                ) : (
                                  <span style={{ color: '#9CA3AF' }}>-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      </React.Fragment>
                    );
                  });
                } else {
                  // Original single-row display for other markets
                  return teamData.players.map((player, index) => {
                    const isFirstRowForPlayer = index === 0 || teamData.players[index - 1].name !== player.name;

                    return (
                      <tr key={player.tierKey || player.id || `${player.name}-${index}`}>
                        <td style={{ textAlign: 'left', padding: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {isFirstRowForPlayer && (
                                <>
                                  <img
                                    src={getTeamLogoUrl(teamData.logo)}
                                    alt={teamData.logo}
                                    style={{ width: '16px', height: '16px' }}
                                    onError={(e) => {
                                      e.target.style.visibility = 'hidden';
                                      e.target.style.width = '0px';
                                      e.target.style.height = '0px';
                                      e.target.style.margin = '0px';
                                    }}
                                  />
                                  <span
                                    onContextMenu={handleNameContextMenu(player)}
                                    onClick={() => {
                                      if (onPlayerClick) {
                                        onPlayerClick(
                                          safeGame.gameIDmlb,
                                          player.playerId,
                                          player.name,
                                          selectedMarket,
                                          safeGame.away,
                                          safeGame.home,
                                          player.team || player.teamId,
                                          player.position || player.positionCode || 'N/A'
                                        );
                                      }
                                    }}
                                    style={{
                                      cursor: onPlayerClick ? 'pointer' : 'default'
                                    }}
                                  >
                                    {formatPlayerName(player.name, player)}
                                  </span>
                                  {selectedMarket !== 'player_first_basket' && selectedMarket !== 'player_first_team_basket' && selectedMarket !== 'player_double_double' && selectedMarket !== 'player_triple_double' && renderPositionBadge(player, safeGame)}
                                </>
                              )}
                              {!isFirstRowForPlayer && (
                                <span style={{ marginLeft: '22px', fontSize: '0.85rem', color: '#6b7280' }}>
                                  {/* Empty space for alignment */}
                                </span>
                              )}
                            </div>
                            {(selectedMarket === 'player_first_basket' || selectedMarket === 'player_first_team_basket' || selectedMarket === 'player_double_double' || selectedMarket === 'player_triple_double') ? (
                              isFirstRowForPlayer && (
                                <span style={{ flexShrink: 0, marginLeft: '8px' }}>
                                  {renderPositionBadge(player, safeGame)}
                                </span>
                              )
                            ) : (
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '8px' }}>
                                {(() => {
                                  if (player.point !== null && player.point !== undefined) {
                                    return player.point === 'yes' ? 'Yes' :
                                           player.point === 'no' ? 'No' :
                                           `${player.point}`;
                                  }
                                  return '';
                                })()}
                              </div>
                            )}
                          </div>
                        </td>
                        {bookLogos.map(book => {
                          const odds = player[`${selectedMarket}_${book}`];
                          const bestBookPrice = player[`${selectedMarket}_bestBookPrice`];
                          const bestBookDiff = recalcEdgeOdds
                            ? getEdgeForPlayerBook(player, book, selectedMarket, allBookmakers)
                            : player[`${selectedMarket}_bestBookDiff`];
                          const bookCount = bookLogos.length;

                          const cellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, bookCount);

                          return (
                            <td
                              key={`${selectedMarket}-${book}-${index}`}
                              className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, null, 'yes')}
                              style={{ ...oddsCellStyle, ...cellStyle, opacity: player[`${selectedMarket}_${book}_isAvailable`] === false ? 0.25 : undefined }}
                              onClick={() => onOddsClick && onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position, null, 'yes')}
                              onContextMenu={(e) => onOddsContextMenu && onOddsContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position, null, 'yes')}
                            >
                              {odds ? (
                                <div style={{ fontSize: '0.9rem' }}>
                                  {odds > 0 ? `+${odds}` : odds}
                                </div>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  });
                }
              })()}
            </tbody>
          </Table>
        </div>
        ))}
      </div>
      </>
      )}
    </div>
  );
};

export default BasketballDataTable;